import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { AuthLayout, AuthLink } from "@/components/auth/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AUTH } from "@/constants/testIds";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await register({
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
      });
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "Unable to create your account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Start your individual agent workspace on AgentHQ."
      footer={<>Already have an account? <AuthLink to="/login" testId={AUTH.toSignin}>Sign in</AuthLink></>}
    >
      <form onSubmit={onSubmit} className="space-y-5" data-testid={AUTH.signupPage} noValidate>
        {error && (
          <Alert variant="destructive" data-testid={AUTH.error}>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="first">First name</Label>
            <Input id="first" required value={form.first_name} onChange={set("first_name")} data-testid={AUTH.firstName} placeholder="Jordan" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last">Last name</Label>
            <Input id="last" required value={form.last_name} onChange={set("last_name")} data-testid={AUTH.lastName} placeholder="Avery" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={set("email")} data-testid={AUTH.email} placeholder="you@example.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" autoComplete="new-password" required value={form.password} onChange={set("password")} data-testid={AUTH.password} placeholder="At least 8 characters" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={set("confirm")} data-testid={AUTH.confirmPassword} placeholder="Re-enter password" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid={AUTH.submit}>
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating…</> : "Create account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
